"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import { useLocale } from "next-intl";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import Link from "next/link";
import { Button } from "./button";
import { Logo } from "./Logo";
import { forgotPasswordAction } from "@/app/actions/password-reset";

const t = {
  en: {
    title: "Reset your password",
    subtitle: "Enter your email and we'll send you a link to reset your password.",
    emailLabel: "Email address",
    emailPlaceholder: "Enter your email",
    submit: "Send reset link",
    submitting: "Sending...",
    backToSignIn: "Back to sign in",
    successTitle: "Check your email",
    successBody: "If an account exists with that email, we've sent you a link to reset your password.",
    unexpectedError: "An unexpected error occurred",
    somethingWentWrong: "Something went wrong",
  },
  de: {
    title: "Passwort zurücksetzen",
    subtitle: "Geben Sie Ihre E-Mail ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "E-Mail eingeben",
    submit: "Link senden",
    submitting: "Wird gesendet...",
    backToSignIn: "Zurück zur Anmeldung",
    successTitle: "E-Mail überprüfen",
    successBody: "Wenn ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet.",
    unexpectedError: "Ein unerwarteter Fehler ist aufgetreten",
    somethingWentWrong: "Etwas ist schiefgelaufen",
  },
};

const formSchema = z.object({
  email: z
    .string({ required_error: "Please enter email" })
    .email("Please enter valid email")
    .min(1, "Please enter email"),
});

type ForgotPasswordForm = z.infer<typeof formSchema>;

export function ForgotPasswordForm() {
  const locale = useLocale();
  const c = locale === "de" ? t.de : t.en;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: ForgotPasswordForm) {
    setIsLoading(true);
    setError(null);

    try {
      const result = await forgotPasswordAction(values.email);

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || c.somethingWentWrong);
      }
    } catch (e) {
      setError(c.unexpectedError);
    } finally {
      setIsLoading(false);
    }
  }

  if (success) {
    return (
      <div className="flex items-center w-full justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div className="flex">
            <Logo />
          </div>
          <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
            {c.successTitle}
          </h2>
          <p className="mt-4 text-neutral-600 dark:text-neutral-400">
            {c.successBody}
          </p>
          <Link
            href="/login"
            className="mt-6 inline-block text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
          >
            {c.backToSignIn}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <div className="flex items-center w-full justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div>
            <div className="flex">
              <Logo />
            </div>
            <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
              {c.title}
            </h2>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              {c.subtitle}
            </p>
          </div>

          <div className="mt-10">
            <div>
              {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark"
                        >
                          {c.emailLabel}
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <input
                              id="email"
                              type="email"
                              placeholder={c.emailPlaceholder}
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <Button className="w-full" disabled={isLoading}>
                    {isLoading ? c.submitting : c.submit}
                  </Button>
                  <p className="text-sm text-neutral-500 text-center mt-4">
                    <Link href="/login" className="text-black dark:text-white">
                      {c.backToSignIn}
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
}
